import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { Spinner } from '@openedx/paragon';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

/**
 * Route guard for per-organisation feature toggles. Renders children only when
 * the named `features_*` flag is enabled; otherwise redirects to the dashboard.
 *
 * While the toggle row is still loading we render a spinner rather than
 * redirecting, so a deep link isn't bounced before the flag is known.
 *
 * @param {string} flag - the `features_*` key to check.
 * @param {boolean} [defaultOn=false] - when true the flag is treated as enabled
 *   unless explicitly 'false' (matches the discover/My-Learning default-on
 *   semantics for Programs); when false the flag must be explicitly true.
 */
const FeatureGate = ({ flag, defaultOn, children }) => {
  const featuresToggled = getFeatureToggle();

  if (featuresToggled.loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" screenReaderText="Loading" />
      </div>
    );
  }

  const enabled = defaultOn
    ? featuresToggled[flag] !== false
    : featuresToggled[flag] === true;

  return enabled ? children : <Navigate to="/" replace />;
};

FeatureGate.propTypes = {
  flag: PropTypes.string.isRequired,
  defaultOn: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

FeatureGate.defaultProps = {
  defaultOn: false,
};

export default FeatureGate;
